import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Button, 
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import taxConfigService from '../../services/taxConfigService';
import PageHeader from '../../components/ui/PageHeader';
import { MagicLoader } from '../../design-system';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const TaxConfiguration = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotificationStore();
  const { user, token } = useAuthStore();
  
  console.log('TaxConfiguration component mounted');
  console.log('Current user permissions:', user?.permissions);
  console.log('Current user role:', user?.role);
  
  // State
  const [loading, setLoading] = useState(true);
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taxTypeDialogOpen, setTaxTypeDialogOpen] = useState(false);
  const [bracketDialogOpen, setBracketDialogOpen] = useState(false);
  const [selectedTaxType, setSelectedTaxType] = useState(null);
  const [selectedBracket, setSelectedBracket] = useState(null);
  const [bracketIndex, setBracketIndex] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [formData, setFormData] = useState({
    regionCode: '',
    regionName: '',
    countryCode: '',
    countryName: '',
    currency: {
      code: 'EGP',
      symbol: 'E£',
      decimals: 2
    }
  });
  const [taxTypeData, setTaxTypeData] = useState({
    name: '',
    code: '',
    description: '',
    rate: '',
    calculationMethod: 'percentage',
    isProgresssive: false,
    brackets: [],
    isActive: true
  });
  const [bracketData, setBracketData] = useState({
    minIncome: '',
    maxIncome: '',
    rate: '',
    fixedAmount: '0'
  });

  // Fetch data
  const fetchData = async () => {
    try {
      console.log('Starting to fetch tax configurations...');
      console.log('User token exists:', !!token);
      
      const [configsResponse, templatesResponse, validationResponse] = await Promise.all([
        taxConfigService.getAllTaxConfigs({ isTemplate: false }),
        taxConfigService.getTaxTemplates(),
        taxConfigService.validateTaxConfiguration()
      ]);
      
      console.log('Configs Response:', configsResponse);
      console.log('Templates Response:', templatesResponse);
      console.log('Validation Response:', validationResponse);
      
      // Handle response data properly - check if data is nested
      // The backend returns { status: 'success', data: [...] } format
      console.log('Raw configsResponse structure:', configsResponse);
      console.log('Raw templatesResponse structure:', templatesResponse);
      console.log('configsResponse.data:', configsResponse?.data);
      console.log('templatesResponse.data:', templatesResponse?.data);
      
      // Handle different response formats
      // getAllTaxConfigs returns { data: [...] } 
      // getTaxTemplates returns [...] directly
      const configs = configsResponse?.data || configsResponse || [];
      const temps = templatesResponse?.data || templatesResponse || [];
      const validation = validationResponse?.data || validationResponse || null;
      
      console.log('Processed configs:', configs);
      console.log('Processed templates:', temps);
      console.log('Validation result:', validation);
      console.log('Setting state - configs length:', configs.length);
      console.log('Setting state - templates length:', temps.length);
      
      setTaxConfigs(Array.isArray(configs) ? configs : []);
      setTemplates(Array.isArray(temps) ? temps : []);
      setValidationResult(validation);
      
      console.log('State set. Now checking for selection...');
      
      // Select first available item (config or template)
      if (Array.isArray(configs) && configs.length > 0) {
        console.log('Selecting first config:', configs[0]);
        setSelectedConfig(configs[0]);
      } else if (Array.isArray(temps) && temps.length > 0) {
        console.log('Selecting first template:', temps[0]);
        setSelectedConfig(temps[0]);
      } else {
        console.log('No configs or templates found to select');
      }
    } catch (error) {
      console.error('Error fetching tax configuration:', error);
      console.error('Error details:', error.response || error);
      showError('Failed to load tax configuration');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useEffect running, calling fetchData...');
    setLoading(true);
    fetchData();
  }, []);

  // Handlers
  const handleCreateFromTemplate = async (templateId) => {
    try {
      const template = templates.find(t => t._id === templateId);
      if (!template) return;
      
      setFormData({
        regionCode: '',
        regionName: '',
        countryCode: template.countryCode,
        countryName: template.countryName,
        currency: template.currency
      });
      setDialogOpen(true);
    } catch (error) {
      showError('Failed to load template');
    }
  };

  const handleSaveConfig = async () => {
    try {
      if (formData._id) {
        // Update existing
        await taxConfigService.updateTaxConfig(formData._id, formData);
        showSuccess('Tax configuration updated successfully');
      } else {
        // Create new
        if (formData.templateId) {
          await taxConfigService.createFromTemplate({
            templateId: formData.templateId,
            regionCode: formData.regionCode,
            regionName: formData.regionName
          });
        } else {
          await taxConfigService.createTaxConfig(formData);
        }
        showSuccess('Tax configuration created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save tax configuration');
    }
  };

  const handleDeleteConfig = async () => {
    try {
      if (!selectedConfig) return;
      
      await taxConfigService.deleteTaxConfig(selectedConfig._id);
      showSuccess('Tax configuration deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedConfig(null);
      fetchData();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to delete tax configuration');
    }
  };

  const handleAddTaxType = async () => {
    try {
      if (!selectedConfig) return;
      
      await taxConfigService.addTaxType(selectedConfig._id, taxTypeData);
      showSuccess('Tax type added successfully');
      setTaxTypeDialogOpen(false);
      setTaxTypeData({
        name: '',
        code: '',
        description: '',
        rate: '',
        calculationMethod: 'percentage',
        isProgresssive: false,
        brackets: [],
        isActive: true
      });
      fetchData();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to add tax type');
    }
  };

  const handleUpdateTaxType = async () => {
    try {
      if (!selectedConfig || !selectedTaxType) return;
      
      await taxConfigService.updateTaxType(selectedConfig._id, selectedTaxType._id, taxTypeData);
      showSuccess('Tax type updated successfully');
      setTaxTypeDialogOpen(false);
      setSelectedTaxType(null);
      fetchData();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update tax type');
    }
  };

  const handleDeleteTaxType = async (taxTypeId) => {
    try {
      if (!selectedConfig) return;
      
      await taxConfigService.removeTaxType(selectedConfig._id, taxTypeId);
      showSuccess('Tax type removed successfully');
      fetchData();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove tax type');
    }
  };

  const handleSaveBracket = () => {
    const newBracket = {
      minIncome: parseFloat(bracketData.minIncome),
      maxIncome: bracketData.maxIncome ? parseFloat(bracketData.maxIncome) : null,
      rate: parseFloat(bracketData.rate),
      fixedAmount: parseFloat(bracketData.fixedAmount) || 0
    };

    let updatedBrackets = [...taxTypeData.brackets];
    
    if (bracketIndex !== null) {
      // Update existing bracket
      updatedBrackets[bracketIndex] = newBracket;
    } else {
      // Add new bracket
      updatedBrackets.push(newBracket);
    }

    // Sort brackets by minIncome
    updatedBrackets.sort((a, b) => a.minIncome - b.minIncome);

    setTaxTypeData({
      ...taxTypeData,
      brackets: updatedBrackets
    });

    setBracketDialogOpen(false);
    setBracketData({
      minIncome: '',
      maxIncome: '',
      rate: '',
      fixedAmount: '0'
    });
    setBracketIndex(null);
  };

  const handleEditBracket = (bracket, index) => {
    setBracketData({
      minIncome: bracket.minIncome.toString(),
      maxIncome: bracket.maxIncome ? bracket.maxIncome.toString() : '',
      rate: bracket.rate.toString(),
      fixedAmount: bracket.fixedAmount ? bracket.fixedAmount.toString() : '0'
    });
    setBracketIndex(index);
    setBracketDialogOpen(true);
  };

  const handleDeleteBracket = (index) => {
    const updatedBrackets = taxTypeData.brackets.filter((_, i) => i !== index);
    setTaxTypeData({
      ...taxTypeData,
      brackets: updatedBrackets
    });
  };

  const openTaxTypeDialog = (taxType = null) => {
    if (taxType) {
      setSelectedTaxType(taxType);
      setTaxTypeData({
        name: taxType.name,
        code: taxType.code,
        description: taxType.description || '',
        rate: taxType.rate || '',
        calculationMethod: taxType.calculationMethod || 'percentage',
        isProgresssive: taxType.isProgresssive || false,
        brackets: taxType.brackets || [],
        isActive: taxType.isActive
      });
    } else {
      setSelectedTaxType(null);
      setTaxTypeData({
        name: '',
        code: '',
        description: '',
        rate: '',
        calculationMethod: 'percentage',
        isProgresssive: false,
        brackets: [],
        isActive: true
      });
    }
    setTaxTypeDialogOpen(true);
  };

  if (loading) {
    return <MagicLoader />;
  }
  
  console.log('TaxConfiguration render:', {
    loading,
    taxConfigsLength: taxConfigs.length,
    templatesLength: templates.length,
    hasSelectedConfig: !!selectedConfig,
    selectedConfigName: selectedConfig?.regionName,
    validationResult
  });
  
  console.log('Detailed state:', {
    taxConfigs,
    templates,
    selectedConfig
  });

  return (
    <Box>
      <PageHeader
        title="Tax Configuration"
        subtitle="Manage tax settings for all regions and countries"
        actions={[
          {
            label: 'Initialize Templates',
            icon: <SettingsIcon />,
            variant: 'outlined',
            onClick: async () => {
              try {
                await taxConfigService.initializeTemplates();
                showSuccess('Tax templates initialized successfully');
                fetchData();
              } catch (error) {
                showError('Failed to initialize tax templates');
              }
            }
          }
        ]}
        primaryAction={{
          label: 'Add Configuration',
          icon: <AddIcon />,
          onClick: () => {
            setFormData({
              regionCode: '',
              regionName: '',
              countryCode: '',
              countryName: '',
              currency: {
                code: 'EGP',
                symbol: 'E£',
                decimals: 2
              }
            });
            setDialogOpen(true);
          }
        }}
      />

      {/* Validation Alert */}
      {validationResult && !validationResult.isValid && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={fetchData}>
              Refresh
            </Button>
          }
        >
          <Typography variant="subtitle2" gutterBottom>
            Tax Configuration Issues:
          </Typography>
          <List dense>
            {validationResult.issues.map((issue, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemText
                  primary={issue.message}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: issue.type === 'error' ? 'error' : 'warning'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Configuration List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tax Configurations
              </Typography>
              <List>
                {taxConfigs.map((config) => (
                  <ListItem
                    key={config._id}
                    button
                    selected={selectedConfig?._id === config._id}
                    onClick={() => setSelectedConfig(config)}
                  >
                    <ListItemText
                      primary={config.regionName}
                      secondary={`${config.countryName} (${config.regionCode})`}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        size="small"
                        label={config.isActive ? 'Active' : 'Inactive'}
                        color={config.isActive ? 'success' : 'default'}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              {templates.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Available Templates
                  </Typography>
                  <List dense>
                    {templates.map((template) => (
                      <ListItem key={template._id}>
                        <ListItemText
                          primary={template.regionName}
                          secondary={template.countryName}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setFormData({
                                ...template,
                                templateId: template._id,
                                regionCode: '',
                                regionName: ''
                              });
                              handleCreateFromTemplate(template._id);
                            }}
                          >
                            <CopyIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration Details */}
        <Grid item xs={12} md={8}>
          {selectedConfig ? (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    {selectedConfig.regionName} Tax Configuration
                  </Typography>
                  <Box>
                    <IconButton onClick={() => {
                      setFormData(selectedConfig);
                      setDialogOpen(true);
                    }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={selectedConfig.isTemplate}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                  <Tab label="General" />
                  <Tab label="Tax Types" />
                  <Tab label="Settings" />
                </Tabs>

                {/* General Tab */}
                {activeTab === 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Region Code
                        </Typography>
                        <Typography variant="body1">
                          {selectedConfig.regionCode}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Country
                        </Typography>
                        <Typography variant="body1">
                          {selectedConfig.countryName} ({selectedConfig.countryCode})
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Currency
                        </Typography>
                        <Typography variant="body1">
                          {selectedConfig.currency.code} ({selectedConfig.currency.symbol})
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Status
                        </Typography>
                        <Chip
                          label={selectedConfig.isActive ? 'Active' : 'Inactive'}
                          color={selectedConfig.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Tax Types Tab */}
                {activeTab === 1 && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">
                        Tax Types
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => openTaxTypeDialog()}
                      >
                        Add Tax Type
                      </Button>
                    </Box>

                    {selectedConfig.taxTypes.map((taxType) => (
                      <Accordion key={taxType._id}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Typography sx={{ flexGrow: 1 }}>
                              {taxType.name} ({taxType.code})
                            </Typography>
                            {taxType.isProgresssive ? (
                              <Chip label="Progressive" size="small" color="info" />
                            ) : (
                              <Chip label={`${taxType.rate}%`} size="small" />
                            )}
                            <Chip
                              label={taxType.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={taxType.isActive ? 'success' : 'default'}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box>
                            {taxType.description && (
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                {taxType.description}
                              </Typography>
                            )}
                            
                            {taxType.isProgresssive && taxType.brackets.length > 0 && (
                              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Min Income</TableCell>
                                      <TableCell>Max Income</TableCell>
                                      <TableCell>Rate</TableCell>
                                      <TableCell>Fixed Amount</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {taxType.brackets.map((bracket, index) => (
                                      <TableRow key={index}>
                                        <TableCell>{bracket.minIncome.toLocaleString()}</TableCell>
                                        <TableCell>
                                          {bracket.maxIncome ? bracket.maxIncome.toLocaleString() : 'Unlimited'}
                                        </TableCell>
                                        <TableCell>{bracket.rate}%</TableCell>
                                        <TableCell>{bracket.fixedAmount || 0}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}

                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => openTaxTypeDialog(taxType)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteTaxType(taxType._id)}
                                disabled={taxType.isDefault}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}

                {/* Settings Tab */}
                {activeTab === 2 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Tax Calculation Settings
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Rounding Method
                        </Typography>
                        <Typography variant="body1">
                          {selectedConfig.taxCalculationSettings?.roundingMethod || 'round'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Calculate on Gross
                        </Typography>
                        <Typography variant="body1">
                          {selectedConfig.taxCalculationSettings?.calculateOnGross ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {selectedConfig.filingRequirements && (
                      <>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                          Filing Requirements
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Filing Frequency
                            </Typography>
                            <Typography variant="body1">
                              {selectedConfig.filingRequirements.filingFrequency}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Filing Deadline
                            </Typography>
                            <Typography variant="body1">
                              Day {selectedConfig.filingRequirements.filingDeadline} of each period
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="textSecondary">
                    Select a tax configuration to view details
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Configuration Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formData._id ? 'Edit Tax Configuration' : 'New Tax Configuration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Region Code"
                value={formData.regionCode}
                onChange={(e) => setFormData({ ...formData, regionCode: e.target.value.toUpperCase() })}
                disabled={!!formData._id}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Region Name"
                value={formData.regionName}
                onChange={(e) => setFormData({ ...formData, regionName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country Code"
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value.toUpperCase() })}
                disabled={!!formData.templateId}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country Name"
                value={formData.countryName}
                onChange={(e) => setFormData({ ...formData, countryName: e.target.value })}
                disabled={!!formData.templateId}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Currency Code"
                value={formData.currency?.code || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  currency: { ...formData.currency, code: e.target.value.toUpperCase() }
                })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Currency Symbol"
                value={formData.currency?.symbol || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  currency: { ...formData.currency, symbol: e.target.value }
                })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Decimals"
                type="number"
                value={formData.currency?.decimals || 2}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  currency: { ...formData.currency, decimals: parseInt(e.target.value) || 2 }
                })}
                inputProps={{ min: 0, max: 4 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveConfig} variant="contained">
            {formData._id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tax Type Dialog */}
      <Dialog open={taxTypeDialogOpen} onClose={() => setTaxTypeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTaxType ? 'Edit Tax Type' : 'Add Tax Type'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Type Name"
                value={taxTypeData.name}
                onChange={(e) => setTaxTypeData({ ...taxTypeData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code"
                value={taxTypeData.code}
                onChange={(e) => setTaxTypeData({ ...taxTypeData, code: e.target.value.toUpperCase() })}
                disabled={!!selectedTaxType}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={taxTypeData.description}
                onChange={(e) => setTaxTypeData({ ...taxTypeData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Calculation Method</InputLabel>
                <Select
                  value={taxTypeData.calculationMethod}
                  onChange={(e) => setTaxTypeData({ ...taxTypeData, calculationMethod: e.target.value })}
                  label="Calculation Method"
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                  <MenuItem value="progressive">Progressive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {taxTypeData.calculationMethod === 'percentage' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tax Rate"
                  type="number"
                  value={taxTypeData.rate}
                  onChange={(e) => setTaxTypeData({ ...taxTypeData, rate: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={taxTypeData.isActive}
                    onChange={(e) => setTaxTypeData({ ...taxTypeData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>

            {taxTypeData.calculationMethod === 'progressive' && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">Tax Brackets</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setBracketData({
                        minIncome: '',
                        maxIncome: '',
                        rate: '',
                        fixedAmount: '0'
                      });
                      setBracketIndex(null);
                      setBracketDialogOpen(true);
                    }}
                  >
                    Add Bracket
                  </Button>
                </Box>
                
                {taxTypeData.brackets.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Min Income</TableCell>
                          <TableCell>Max Income</TableCell>
                          <TableCell>Rate (%)</TableCell>
                          <TableCell>Fixed Amount</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {taxTypeData.brackets.map((bracket, index) => (
                          <TableRow key={index}>
                            <TableCell>{bracket.minIncome.toLocaleString()}</TableCell>
                            <TableCell>
                              {bracket.maxIncome ? bracket.maxIncome.toLocaleString() : 'Unlimited'}
                            </TableCell>
                            <TableCell>{bracket.rate}%</TableCell>
                            <TableCell>{bracket.fixedAmount || 0}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleEditBracket(bracket, index)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteBracket(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxTypeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={selectedTaxType ? handleUpdateTaxType : handleAddTaxType} 
            variant="contained"
          >
            {selectedTaxType ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bracket Dialog */}
      <Dialog open={bracketDialogOpen} onClose={() => setBracketDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bracketIndex !== null ? 'Edit Tax Bracket' : 'Add Tax Bracket'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Income"
                type="number"
                value={bracketData.minIncome}
                onChange={(e) => setBracketData({ ...bracketData, minIncome: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Income"
                type="number"
                value={bracketData.maxIncome}
                onChange={(e) => setBracketData({ ...bracketData, maxIncome: e.target.value })}
                helperText="Leave empty for unlimited"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Rate"
                type="number"
                value={bracketData.rate}
                onChange={(e) => setBracketData({ ...bracketData, rate: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fixed Amount"
                type="number"
                value={bracketData.fixedAmount}
                onChange={(e) => setBracketData({ ...bracketData, fixedAmount: e.target.value })}
                helperText="Additional fixed tax amount"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBracketDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveBracket} variant="contained">
            {bracketIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Tax Configuration"
        message={`Are you sure you want to delete the tax configuration for ${selectedConfig?.regionName}?`}
        onConfirm={handleDeleteConfig}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default TaxConfiguration;